<template>
  <div :class="{open:isOpen, active:isActive}"
       class="collapsible-container">
    <header class="container-header" ref="header">
      <div v-if="draggable" class="tag-handle"></div>
      <div @click="navigateToLink" class="clickable-wrapper" :class="{link:!isOpen}">
        <div v-if="hasImage" class="image-box">
          <slot name="image"/>
        </div>
        <div class="title-block">
          <editable-string
            class="title"
            ref="title"
            :value="title"
            :defaultValue="defaultTitle"
            :edit="editTitle"
            @input="updateTitle"
            @escape="isOpen=false">
          </editable-string>
          <div v-if="hasSubtitle" class="sub-title">
            <slot name="sub-title"/>
          </div>
        </div>
      </div>
      <div class="tools">
        <button @click="isOpen = !isOpen" class="small">
          <i class="drop-down" :class="{'open':isOpen}"></i>
        </button>
      </div>
    </header>
    <div v-if="hasBadge" class="badge">
      <slot name="badge"/>
    </div>
    <transition-expand>
      <div v-show="isOpen" ref="content" class="drawer">
        <div class="content-box">
          <slot name="content"/>
        </div>
        <footer class="tools">
          <slot name="content-tools"/>
        </footer>
      </div>
    </transition-expand>
  </div>
</template>

<script>
import TransitionExpand from './TransitionExpand'
import EditableString from './inputs/EditableString'

export default {
  name: 'collapsible-container',
  components: { TransitionExpand, EditableString },
  props: {
    id: String,
    title: { type: String, required: true },
    defaultTitle: String,
    linkName: String,
    draggable: Boolean
  },
  data () {
    return {
      isOpen: false,
      tempColor: null,
      editTitle: false
    }
  },
  computed: {
    isActive () {
      return this.$route.params.id === this.id
    },

    hasImage () {
      return !!this.$slots['image']
    },

    hasSubtitle () {
      return !!this.$slots['sub-title']
    },

    hasBadge () {
      return !!this.$slots['badge']
    }
  },
  methods: {
    navigateToLink () {
      if (this.linkName) {
        if (!this.isOpen) {
          this.$router.push({ name: this.linkName, params: { id: this.id } })
        }
      } else {
        this.isOpen = !this.isOpen
      }
    },

    deleteContainer () {
      this.$emit('delete', this.id)
    },

    updateTitle (value) {
      this.$emit('updateTitle', value)
    },

    setWidth () {
      const content = this.$refs.content
      const header = this.$refs.header
      if (!header || !content) return
      const box = content.querySelector('.content-box')
      if (!box) return

      header.style.width = 'auto'

      const display = content.style.display
      content.style.display = null
      content.style.visibility = 'hidden'

      const boxWidth = box.getBoundingClientRect().width
      header.style.width = boxWidth + 'px'

      content.style.visibility = null
      content.style.display = display
    }
  },
  watch: {
    isOpen (value) {
      this.editTitle = value
    },
    id () {
      this.setWidth()
    }
  },

  mounted () {
    this.setWidth()
    window.addEventListener('resize', this.setWidth)
  }
}
</script>
<style lang="scss">
  $container-corner-radius: 0 !default;
  $container-color: #fff !default;
  @import "../../../scss/vars";
  @import "../../../scss/mixins";

  $container-padding: $basic-padding;

  .collapsible-container {
    padding: 0;
    max-width: 100%;
    position: relative;
    min-height: $basic-size;
    margin-bottom: 5px;
    border-radius: $container-corner-radius;
    @include medium-text;

    background: $container-color;

    &:before {
      content: '';
      position: absolute;
      width: 2rem;
      height: 100%;
      top: -0px;
      left: 0;
      background: $container-color;
      border-top-left-radius: $container-corner-radius;
      border-bottom-left-radius: $container-corner-radius;
      transition: left 0.1s;
      z-index: -1;
    }

    &.active {
      &:before {
        left: -1.5rem;
      }
    }

    .container-header {
      position: relative;
      display: flex;
      align-items: center;
      min-height: $basic-size - $container-padding * 2;
      padding: 0;
      background: $container-color;
      border-radius: $container-corner-radius;

      .clickable-wrapper {
        flex-shrink: 1;
        min-width: 0;
        display: flex;
        align-items: center;
        max-width: 100%;
      }

      .image-box {
        flex-grow: 0;
      }

      .title-block {
        $sub-padding: 0;
        display: block;
        padding: 0 $basic-padding;

        flex-basis: 0;
        min-width: 0;
        width: 100%;
        flex-grow: 1;

        .title {
          box-sizing: content-box;
          min-width: 0;
          padding: 0 $sub-padding;
          height: $basic-size;
        }

        .sub-title {
          width: 100%;
          min-width: 0;
          height: $basic-size / 2;
          padding: 0 $sub-padding;
          @include micro-text;
          @include text-ellipsis;
        }
      }

      .clickable-wrapper {
        min-width: 0;
        background: transparent;
        margin: 0;

        &.link {
          cursor: pointer;
        }
      }

    }

    .tools {
      flex-shrink: 0;
      align-self: stretch;

      display: flex;
      align-items: flex-start;
      justify-content: flex-end;

      margin-left: auto;
      font-size: 80%;

      button {
        background: transparent;
        box-shadow: none;

        &:not(:last-child) {
          margin-right: $container-padding;
        }

        &:first-child {
          margin-right: auto;
        }
      }
    }

    .drawer {
      background: $dark-content-bg;

      .content-box {
        padding: $container-padding;
      }
    }

    .badge {
      position: absolute;
      right: $container-padding;
      top: $container-padding;
      border-radius: 50%;
      overflow: hidden;
    }

    footer {
      background: transparent;
      padding: 0;
    }
  }
</style>
